import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from './Logo';
import { NotificationBell } from './NotificationBell';
import { ChevronDown, Menu, X, Search } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Search-enabled dropdown body. Default state (empty query) shows the curated
// roster; typing at least 2 characters queries /public/memorial-search for
// published + demo memorials across the platform. Keeps the dropdown calm and
// premium — single input, compact result rows, no clutter.
function StillHereSearch({ onNavigate, autoFocus = false }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Focus the input only when explicitly requested (desktop dropdown).
  // On mobile, auto-focusing pops the keyboard up the moment the drawer
  // opens — which felt to users like the menu had "navigated to search"
  // instead of showing the options.
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Debounced search — 220ms after the user stops typing.
  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await axios.get(`${API}/public/memorial-search`, {
          params: { q: q.trim() },
        });
        if (!cancelled) setResults(data.results || []);
      } catch {
        if (!cancelled) setError('Search unavailable just now.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [q]);

  const formatYears = (r) => {
    const b = (r.date_of_birth || '').slice(0, 4);
    const d = (r.date_of_passing || '').slice(0, 4);
    if (!b && !d) return '';
    return `${b || '—'} — ${d || '—'}`;
  };

  return (
    <div className="flex flex-col">
      <div className="px-5 pt-4 pb-3 border-b border-[hsl(var(--primary)/0.08)]">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[hsl(var(--primary)/0.55)] mb-2">
          Search memorials
        </div>
        <div className="relative">
          <Search
            className="absolute left-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(var(--primary)/0.5)]"
            aria-hidden
          />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name…"
            data-testid="still-here-search-input"
            className="w-full bg-transparent border-0 border-b border-[hsl(var(--primary)/0.2)] pl-5 pr-1 py-2 text-[15px] font-light text-[hsl(var(--primary))] placeholder:text-[hsl(var(--primary)/0.4)] outline-none focus:border-[hsl(var(--primary))]"
          />
        </div>
      </div>

      <ul
        className="py-2 max-h-80 overflow-y-auto"
        data-testid="still-here-results"
      >
        {loading && (
          <li className="px-5 py-4 text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.45)]">
            Searching…
          </li>
        )}
        {!loading && error && (
          <li className="px-5 py-4 text-sm text-[hsl(var(--destructive))] font-light">
            {error}
          </li>
        )}
        {!loading && !error && results.length === 0 && (
          <li className="px-5 py-5 text-center">
            <div className="font-serif italic text-[hsl(var(--primary)/0.6)] text-sm">
              No memorials found for
              <span className="block text-[hsl(var(--primary))] mt-1">“{q}”</span>
            </div>
          </li>
        )}
        {!loading && !error && results.map((r) => (
          <li key={r.slug} data-testid={`still-here-result-${r.slug}`}>
            <Link
              to={`/m/${r.slug}`}
              onClick={onNavigate}
              data-testid={`still-here-link-${r.slug}`}
              className="flex items-center gap-3 px-5 py-2.5 hover:bg-[hsl(var(--primary)/0.05)] transition-colors"
            >
              <div className="w-9 h-9 rounded-full overflow-hidden bg-[hsl(var(--muted))] border border-[hsl(var(--primary)/0.12)] shrink-0">
                {r.profile_photo_url ? (
                  <img src={r.profile_photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-serif text-[hsl(var(--primary)/0.7)]">
                    {(r.full_name || '?')[0]}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-[15px] text-[hsl(var(--primary))] truncate">
                  {r.full_name}
                </div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.5)] mt-0.5">
                  {formatYears(r)}
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Navbar({ variant = 'default' }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [mobileStillHereOpen, setMobileStillHereOpen] = useState(false);
  const [stillHereOpen, setStillHereOpen] = useState(false);
  const stillHereRef = useRef(null);
  const nav = useNavigate();
  const loc = useLocation();
  const isAdmin = user && (user.role === 'admin' || user.role === 'platform_admin');
  // All navbars are now forest-green anchored. The `variant` prop is kept
  // for backward compatibility but no longer toggles the color scheme.

  // Close the Still-Here dropdown on outside click.
  useEffect(() => {
    if (!stillHereOpen) return;
    const onDoc = (e) => {
      if (stillHereRef.current && !stillHereRef.current.contains(e.target)) {
        setStillHereOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [stillHereOpen]);

  // Click the wordmark/logo in the navbar → land on Home and scroll top.
  // (Plain <Link to="/"> doesn't scroll if you're already on /.)
  const goHome = (e) => {
    if (loc.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const links = [
    { to: '/#how', label: 'How It Works' },
    { to: '/#features', label: 'Features' },
    { to: '/#pricing', label: 'Pricing' },
  ];

  // Forest bar · readable honey links that lift toward warm cream on hover.
  const linkClass = 'text-[#F4E5C2]/90 hover:text-white';

  return (
    <header
      className="w-full sticky top-0 z-30 border-b border-[#C5A059]/12"
      style={{ backgroundColor: '#1A3626' }}
    >
      <div className="ml-container-wide flex items-center justify-between py-4 gap-8">
        <Link to="/" onClick={goHome} data-testid="nav-logo-link" className="flex items-center gap-3.5 shrink-0">
          <Logo tone="cream" size={62} />
          <span
            className="hidden sm:inline font-serif text-[19px] sm:text-[20px] tracking-[0.18em] uppercase text-[#F1DFB2] drop-shadow-[0_1px_0_rgba(0,0,0,0.18)]"
            style={{ fontWeight: 600, fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif' }}
          >
            MyLeaf
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 whitespace-nowrap">
          {links.map((l) => (
            <a
              key={l.to}
              href={l.to}
              onClick={(e) => {
                if (l.to.startsWith('/#') && loc.pathname === '/') {
                  e.preventDefault();
                  const id = l.to.slice(2);
                  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`text-[11px] font-medium tracking-[0.2em] uppercase transition-colors ${linkClass}`}
            >
              {l.label}
            </a>
          ))}

          {/* Still Here — replaces the old Demos link. Hover/click reveals
              the curated roster of memorials a visitor can walk through. */}
          <div className="relative" ref={stillHereRef}>
            <button
              type="button"
              onClick={() => setStillHereOpen((v) => !v)}
              data-testid="nav-link-still-here"
              className={`flex items-center gap-1 text-[11px] font-medium tracking-[0.2em] uppercase transition-colors ${linkClass}`}
              aria-haspopup="menu"
              aria-expanded={stillHereOpen}
            >
              Still Here
              <ChevronDown
                size={12}
                className={`mt-px transition-transform duration-300 ${stillHereOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {stillHereOpen && (
              <div
                role="menu"
                data-testid="still-here-dropdown"
                className="absolute right-0 top-full mt-4 w-80 rounded-md border border-[#C5A059]/25 bg-[#F7F3E8] shadow-[0_30px_60px_-30px_rgba(26,54,38,0.5)] overflow-hidden"
              >
                <StillHereSearch onNavigate={() => setStillHereOpen(false)} autoFocus />
              </div>
            )}
          </div>
        </nav>

        <div className="hidden md:flex items-center gap-6 shrink-0 whitespace-nowrap">
          {user ? (
            <>
              <NotificationBell onDark={true} />
              {/* iter82 — Dashboard link is account-type aware so partners
                  (RP / MP) land on /partners/<role> instead of the family
                  /dashboard view. Fixes the case where approved RPs
                  clicked "Dashboard" expecting their partner dashboard
                  and saw the family "no memorial yet" page instead. */}
              <Link
                to={
                  user?.account_type === 'regional_partner' ? '/partners/regional'
                  : user?.account_type === 'memorial_partner' ? '/partners/memorial'
                  : '/dashboard'
                }
                className={`text-[11px] tracking-[0.2em] uppercase font-medium transition-colors ${linkClass}`}
                data-testid="nav-dashboard-btn"
              >
                Dashboard
              </Link>
              <Link
                to="/account/settings"
                className={`text-[11px] tracking-[0.2em] uppercase font-medium transition-colors ${linkClass}`}
                data-testid="nav-settings-btn"
              >
                Settings
              </Link>
              {/* iter69 — greeting reads first_name → name → email-localpart.
                  No `Friend` fallback: it confused production users who saw
                  the site "auto-naming" them. If everything is empty (e.g.
                  a freshly-created account where Google didn't return a
                  name), show "My Account" instead. */}
              <span
                className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#D9B775] max-w-[180px] truncate"
                title={user.name || user.email || 'My Account'}
                data-testid="nav-user-greeting"
              >
                {(user.first_name
                  || user.name?.split(' ')[0]
                  || user.email?.split('@')[0]
                  || 'My Account')}
              </span>
              <button
                onClick={async () => {
                  await logout();
                  nav('/');
                }}
                className={`text-[11px] tracking-[0.2em] uppercase font-medium transition-colors ${linkClass}`}
                data-testid="nav-logout-btn"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-btn"
                className={`text-[11px] tracking-[0.2em] uppercase font-medium transition-colors ${linkClass}`}
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#C5A059] text-[#1A3626] px-6 py-2.5 text-[11px] font-medium tracking-[0.22em] uppercase transition-all duration-300 hover:bg-[#E8D9B8]"
                data-testid="nav-register-btn"
              >
                Begin a Memorial
              </Link>
            </>
          )}
        </div>

        <div className="md:hidden flex items-center gap-2">
          {user && <NotificationBell onDark={true} />}
          <button
            className="p-2 transition-colors text-[#C5A059] hover:text-[#E8D9B8]"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-menu-open-btn"
          >
            <Menu strokeWidth={1.4} />
          </button>
        </div>
      </div>

      {/* Mobile drawer — cream, sacred */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-[hsl(var(--background))] text-[hsl(var(--primary))] flex flex-col px-8 pt-6 pb-10 overflow-y-auto"
          data-testid="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Main menu"
        >
          <div className="flex items-center justify-between">
            <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-3">
              <Logo tone="green" size={30} />
              <span className="font-serif text-[15px] tracking-[0.28em] uppercase text-[hsl(var(--primary))]">
                MyLeaf
              </span>
            </Link>
            <button
              onClick={() => { setOpen(false); setMobileStillHereOpen(false); }}
              className="p-2 text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))] transition-colors"
              aria-label="Close menu"
              data-testid="nav-menu-close-btn"
            >
              <X strokeWidth={1.4} />
            </button>
          </div>

          <div className="ml-divider-leaf w-16 mt-10" aria-hidden />

          <nav className="mt-10 flex flex-col gap-6" aria-label="Mobile">
            {links.map((l) => (
              <a
                key={l.to}
                href={l.to}
                onClick={(e) => {
                  if (l.to.startsWith('/#') && loc.pathname === '/') {
                    e.preventDefault();
                    const id = l.to.slice(2);
                    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                  }
                  setOpen(false);
                }}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="font-serif font-light text-3xl text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))] transition-colors"
              >
                {l.label}
              </a>
            ))}

            {/* Still Here group on mobile — collapsed by default so the
                drawer doesn't auto-pop the keyboard. Tap to expand. */}
            <div className="pt-2" data-testid="mobile-still-here">
              <button
                type="button"
                onClick={() => setMobileStillHereOpen((v) => !v)}
                className="w-full flex items-center justify-between font-serif font-light text-3xl text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))] transition-colors"
                aria-expanded={mobileStillHereOpen}
                data-testid="mobile-still-here-toggle"
              >
                <span>Still Here</span>
                <ChevronDown
                  size={20}
                  className={`transition-transform ${mobileStillHereOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>
              {mobileStillHereOpen && (
                <div className="mt-4">
                  <StillHereSearch onNavigate={() => setOpen(false)} autoFocus />
                </div>
              )}
            </div>
          </nav>

          <div className="ml-divider-leaf w-16 my-10" aria-hidden />

          <div className="flex flex-col gap-4">
            {user ? (
              <>
                <Link
                  to={
                    user?.account_type === 'regional_partner' ? '/partners/regional'
                    : user?.account_type === 'memorial_partner' ? '/partners/memorial'
                    : '/dashboard'
                  }
                  onClick={() => setOpen(false)}
                  className="ml-btn-primary"
                  data-testid="mobile-nav-dashboard-btn"
                >
                  Dashboard
                </Link>
                <Link
                  to="/account/settings"
                  onClick={() => setOpen(false)}
                  className="ml-btn-outline"
                  data-testid="mobile-nav-settings-btn"
                >
                  Settings
                </Link>
                {isAdmin && (
                  <div data-testid="mobile-admin-section" className="mt-2 pt-3 border-t border-[hsl(var(--primary)/0.12)]">
                    <div className="text-[10px] tracking-[0.28em] uppercase text-[hsl(var(--primary)/0.55)] mb-2 px-1">
                      Platform admin
                    </div>
                    {[
                      { to: '/admin/reports', label: 'Reports & Insights', testid: 'mobile-admin-link-reports' },
                      { to: '/admin/families', label: 'Family Accounts', testid: 'mobile-admin-link-families' },
                      { to: '/admin/partners/applications', label: 'Partner Applications', testid: 'mobile-admin-link-partner-applications' },
                      { to: '/admin/partners/overrides', label: 'Override Contracts', testid: 'mobile-admin-link-override-contracts' },
                      { to: '/admin/promo-codes', label: 'Promo Codes', testid: 'mobile-admin-link-promo-codes' },
                      { to: '/admin/assistance', label: 'Assistance Requests', testid: 'mobile-admin-link-assistance-requests' },
                      { to: '/admin/professional/applications', label: 'Professional (legacy)', testid: 'mobile-admin-link-professional-applications' },
                    ].map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setOpen(false)}
                        data-testid={item.testid}
                        className="block px-1 py-2 text-[14px] text-[hsl(var(--primary))]"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
                <button
                  onClick={async () => {
                    await logout();
                    setOpen(false);
                    nav('/');
                  }}
                  className="ml-btn-outline"
                  data-testid="mobile-nav-logout-btn"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="ml-btn-outline"
                  data-testid="mobile-nav-login-btn"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  onClick={() => setOpen(false)}
                  className="ml-btn-primary"
                  data-testid="mobile-nav-register-btn"
                >
                  Begin a Memorial
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer
      className="mt-24"
      style={{ backgroundColor: '#1A3626' }}
      data-testid="site-footer"
    >
      <div className="ml-container pt-20 pb-10">
        <div className="grid md:grid-cols-[1.2fr_0.8fr_0.8fr] gap-14">
          <div>
            <div className="flex items-center gap-3.5">
              <Logo tone="cream" size={56} />
              <span
                className="font-serif text-[19px] tracking-[0.18em] uppercase text-[#F1DFB2]"
                style={{ fontWeight: 600, fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif' }}
              >
                MyLeaf
              </span>
            </div>
            <p className="mt-6 text-sm text-[#F4E5C2]/85 max-w-sm leading-relaxed font-light">
              A quiet, beautiful home for a life that mattered. Memorial pages, QR plaques,
              family trees, and a private vault — kept with care, for generations.
            </p>
          </div>

          <div>
            <h4 className="text-[10px] font-semibold uppercase tracking-[0.28em] mb-6 text-[#D9B775]">Memorial</h4>
            <ul className="space-y-3 text-sm text-[#F4E5C2] font-light">
              <li><a href="/#how" className="hover:text-white transition-colors">How it works</a></li>
              <li><a href="/#features" className="hover:text-white transition-colors">Features</a></li>
              <li><a href="/#demos" className="hover:text-white transition-colors">Still Here</a></li>
              <li><a href="/#pricing" className="hover:text-white transition-colors">Plans</a></li>
              <li><a href="/find-funeral-homes" className="hover:text-white transition-colors" data-testid="footer-find-funeral-homes">Find funeral homes &amp; cemeteries</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-[10px] font-semibold uppercase tracking-[0.28em] mb-6 text-[#D9B775]">Keep in touch</h4>
            <ul className="space-y-3 text-sm text-[#F4E5C2] font-light">
              <li>myleaflegacy.com</li>
              <li>
                <a
                  href="mailto:admin@myleaflegacy.info"
                  className="hover:text-white transition-colors"
                  data-testid="footer-contact-email"
                >
                  admin@myleaflegacy.info
                </a>
              </li>
              <li className="text-[#F4E5C2]/75 pt-1">
                <Link
                  to="/become-a-partner"
                  className="hover:text-white transition-colors"
                  data-testid="footer-become-a-partner"
                >
                  Cemetery &amp; funeral home partnerships →
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div
          className="mt-16 h-px w-full"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(217,183,117,0.45), transparent)' }}
          aria-hidden
        />

        <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-3 text-[11px] tracking-[0.22em] uppercase text-[#F4E5C2]/85">
          <div>© {new Date().getFullYear()} MyLeaf Legacy</div>
          <div className="font-serif italic tracking-normal normal-case text-[14px] text-[#F4E5C2]">
            Forever Here.
          </div>
          <div>Made with care</div>
        </div>
      </div>
    </footer>
  );
}
